import { useEffect, useRef, type ReactNode } from "react";

import { useFooterReveal } from "@/hooks/useFooterReveal";
import { usePulseScale } from "@/hooks/usePulseScale";

import PulseFooter from "./PulseFooter";

interface Props {
  header: ReactNode;
  children: ReactNode;
  unsubscribeIntent?: boolean;
  onUnsubscribeIntentConsumed?: () => void;
}

export default function PulsePageShell({
  header,
  children,
  unsubscribeIntent = false,
  onUnsubscribeIntentConsumed,
}: Props) {
  const shellRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);

  const scale = usePulseScale(shellRef);
  useFooterReveal(shellRef, contentRef, placeholderRef, footerRef, scale);

  useEffect(() => {
    if (!unsubscribeIntent) return;

    const footer = footerRef.current;
    if (!footer) return;

    const timer = window.setTimeout(() => {
      footer.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [unsubscribeIntent]);

  return (
    <div id="pulse-scale-shell" ref={shellRef} className="pulse-scale-shell origin-top">
      <div className="pulse-page-wrap grid">
        <div
          ref={contentRef}
          className="pulse-content-wrap relative z-[100] col-start-1 row-start-1 grid grid-rows-[auto_1fr] bg-[#fff5ec]"
        >
          {header}
          {children}
        </div>

        <div
          ref={placeholderRef}
          className="pulse-footer-placeholder sticky bottom-0 col-start-1 row-start-1 z-0 self-end"
        >
          <PulseFooter
            ref={footerRef}
            unsubscribeIntent={unsubscribeIntent}
            onUnsubscribeIntentConsumed={onUnsubscribeIntentConsumed}
          />
        </div>
      </div>
    </div>
  );
}
